import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { passkeyApi } from '@api/client';
import * as Icons from '@components/Icons';

const passkeysSupported = typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

type Phase = 'verifying' | 'offer-passkey' | 'registering' | 'error';

/**
 * Lands here from the magic-link email (`/admin/magic-verify?token=…`).
 * Exchanges the token for a session, persists it, and redirects into the admin.
 * Before redirect: if the user has no passkey yet and the browser supports
 * WebAuthn, offer to register one so future logins skip the email.
 */
export function MagicVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeMagicLink, registerPasskey } = useAuth();

  const token = searchParams.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>(token ? 'verifying' : 'error');
  const ran = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects; the token is single-use, so guard it.
    if (ran.current) return;
    ran.current = true;
    if (!token) return;
    void (async () => {
      try {
        await completeMagicLink(token);
        if (!passkeysSupported) {
          void navigate('/admin/bookings', { replace: true });
          return;
        }
        // Only offer when the user has zero passkeys (avoid pestering returning users).
        try {
          const existing = await passkeyApi.list();
          if (existing.length > 0) {
            void navigate('/admin/bookings', { replace: true });
            return;
          }
        } catch {
          // If listing fails (network etc.), skip the offer and continue.
          void navigate('/admin/bookings', { replace: true });
          return;
        }
        setPhase('offer-passkey');
      } catch {
        setPhase('error');
      }
    })();
  }, [token, completeMagicLink, navigate]);

  async function handleRegister() {
    setPhase('registering');
    try {
      await registerPasskey();
    } catch {
      // Cancel / error — just continue. The user can register later from Settings.
    }
    void navigate('/admin/bookings', { replace: true });
  }

  function handleSkip() {
    void navigate('/admin/bookings', { replace: true });
  }

  return (
    <div className="login-page">
      <div className="login-side">
        <div className="login-brand">
          <span className="side-brand-mark">E</span>
          <span>Equipomag Bookings</span>
        </div>
        <div className="login-hero">
          <div className="login-hero-eyebrow">ACCESO SEGURO</div>
          <h1 className="login-hero-title">Verificando tu enlace…</h1>
          <p className="login-hero-body">Te estamos identificando sin contraseña.</p>
        </div>
        <div className="login-foot">MADRID · BARCELONA · VALENCIA · EQUIPOMAG</div>
      </div>

      <div className="login-form-wrap">
        <div className="login-form">
          {phase === 'error' && (
            <>
              <div className="login-form-head">
                <h2 className="login-form-title">Enlace no válido</h2>
                <p className="login-form-sub">
                  El enlace no es válido o ha caducado. Solicita uno nuevo desde el inicio de sesión.
                </p>
              </div>
              <Link
                to="/admin/login"
                className="btn"
                data-variant="primary"
                data-size="lg"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Icons.ChevL size={14} /> Volver al inicio de sesión
              </Link>
            </>
          )}

          {(phase === 'verifying' || phase === 'registering') && (
            <div className="login-form-head">
              <h2 className="login-form-title">
                {phase === 'registering' ? 'Registrando passkey…' : 'Accediendo…'}
              </h2>
              <p className="login-form-sub">
                {phase === 'registering'
                  ? 'Sigue las instrucciones de tu navegador para confirmar el passkey.'
                  : 'Un momento, te estamos llevando al panel.'}
              </p>
            </div>
          )}

          {phase === 'offer-passkey' && (
            <>
              <div className="login-form-head">
                <h2 className="login-form-title">¿Añadir este dispositivo?</h2>
                <p className="login-form-sub">
                  Registra un passkey y entra al panel sin recibir más emails. Usa tu huella, Face ID
                  o el método de seguridad de tu dispositivo.
                </p>
              </div>
              <button
                type="button"
                className="btn"
                data-variant="primary"
                data-size="lg"
                onClick={() => { void handleRegister(); }}
              >
                Sí, añadir passkey
              </button>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                onClick={handleSkip}
              >
                Ahora no
              </button>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--fg-soft)',
                  textAlign: 'center',
                  marginTop: 10,
                  lineHeight: 1.5,
                }}
              >
                Podrás añadirlo o eliminarlo en cualquier momento desde Ajustes.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
